import { useState } from 'react'
import Button from './Button'
import { FEEDBACK_CATEGORIES, submitFeedback } from '../../services/feedbackService'

const MAX_LENGTH = 4000

export default function FeedbackCard() {
  const [category, setCategory] = useState('idea')
  const [rating, setRating] = useState(0)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const canSend = message.trim().length >= 3 && !sending

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!canSend) return
    setSending(true)
    setError('')
    try {
      await submitFeedback({ message, category, rating })
      setSent(true)
      setMessage('')
      setRating(0)
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          'We couldn’t send your feedback just now. Please check your connection and try again.'
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <section
      id="feedback"
      className="rounded-2xl border border-light-gray bg-white shadow-sm p-5 sm:p-6"
    >
      <h2 className="text-base font-semibold mb-1 text-[#5a3e36]">Send feedback</h2>
      <p className="text-sm text-[#7a6f66] mb-4">
        Tell us what to improve, what’s broken, or what you love. We read every message and use it to
        plan the next updates.
      </p>

      {sent ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" role="status">
          Thank you. Your feedback has been sent.
          <button
            type="button"
            className="ml-2 underline font-semibold"
            onClick={() => setSent(false)}
          >
            Send more
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block text-xs font-semibold text-[#7a6f66]">
            Type
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full rounded-lg border border-light-gray bg-white px-3 py-2 text-sm text-[#5a3e36]"
            >
              {FEEDBACK_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <div>
            <div className="text-xs font-semibold text-[#7a6f66]">How are you finding Ruoth? (optional)</div>
            <div className="mt-1 flex gap-1" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                  onClick={() => setRating(rating === n ? 0 : n)}
                  className={`text-2xl leading-none ${n <= rating ? 'text-rich-gold' : 'text-gray-300'}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          <label className="block text-xs font-semibold text-[#7a6f66]">
            Your feedback
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
              rows={5}
              placeholder="What would make Ruoth better for you?"
              className="mt-1 w-full rounded-lg border border-light-gray bg-white px-3 py-2 text-sm text-[#5a3e36]"
            />
            <span className="block text-right text-[11px] text-[#7a6f66]">
              {message.length}/{MAX_LENGTH}
            </span>
          </label>

          {error && (
            <div className="text-sm text-red-600" role="alert">
              {error}
            </div>
          )}

          <Button type="submit" loading={sending} disabled={!canSend} className="justify-center w-full sm:w-auto">
            Send feedback
          </Button>
        </form>
      )}
    </section>
  )
}
